import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import cx from 'classnames';

import {Banner, Icon, Text} from '@albert-io/atomic';
import type {BannerType} from '@albert-io/atomic/molecules/Banner/Banner.react';
import {GuessModelV1} from '@albert-io/models';
import {type GuessCorrectness} from 'resources/augmented/Guess/GuessModel.v1';

import PartialCreditIcon from 'client/components/PartialCreditIcon/PartialCreditIcon';

import {useQuestionTranslationStore} from '../TranslationSection';

import {guessCorrectnessBanner} from '../TranslationSection/TranslationText';

export type GuessStatus = GuessCorrectness | 'unsubmitted';

interface Props {
  additionalCopy?: string;
  guess?: GuessModelV1;
  partialCreditEnabled?: boolean;
  overriddenGuessStatus?: GuessStatus;
  /** 0..1. When set, overrides the percent shown for partial credit (e.g. a teacher's manual score). */
  overriddenGrade?: number;
  className?: string;
}

const guessCorrectnessToBannerType = new Map<GuessStatus, BannerType>([
  ['unsubmitted', 'neutral'],
  ['incorrect', 'negative'],
  ['partiallyCorrect', 'positive'],
  ['correct', 'positive']
]);

const guessCorrectnessToEnglishCopy = new Map<GuessStatus, string>([
  ['unsubmitted', 'No answer was submitted for this question.'],
  ['incorrect', 'Incorrect'],
  ['partiallyCorrect', "You've received partial credit"],
  ['correct', 'Correct']
]);

export default function GuessCorrectnessBanner({
  additionalCopy,
  guess = new GuessModelV1(),
  partialCreditEnabled = false,
  overriddenGuessStatus,
  overriddenGrade,
  className
}: Props) {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  let guessStatus: GuessStatus = guess.getCorrectness?.(partialCreditEnabled);

  if (guess.getId() === '' || guess.isGuessEmpty?.()) {
    guessStatus = 'unsubmitted';
  }

  if (overriddenGuessStatus !== undefined) {
    guessStatus = overriddenGuessStatus;
  }

  const copyMap = showTranslations ? guessCorrectnessBanner : guessCorrectnessToEnglishCopy;
  let bannerCopy = copyMap.get(guessStatus);
  const bannerType = guessCorrectnessToBannerType.get(guessStatus);

  if (additionalCopy !== undefined) {
    if (!bannerCopy?.endsWith('.')) {
      bannerCopy = `${bannerCopy}.`;
    }

    bannerCopy = `${bannerCopy} ${additionalCopy}`;
  }

  return (
    <Banner className={cx('mb-x4', className)} data-testid='result-banner' type={bannerType}>
      <div className='flex w-full justify-between'>
        <div className='flex items-center gap-x2'>
          {['correct', 'incorrect'].includes(guessStatus) && (
            <Icon icon={guessStatus === 'correct' ? 'circle-check' : 'circle-xmark'} />
          )}
          {guessStatus === 'partiallyCorrect' && <PartialCreditIcon />}
          <Text bold color='inherit' size='m'>
            {bannerCopy}
          </Text>
        </div>
        {guessStatus === 'partiallyCorrect' && (
          <Text color='inherit' size='m'>
            {(overriddenGrade ?? guess.getGrade()).toLocaleString(undefined, {style: 'percent'})}{' '}
            {copyMap.get('correct')}
          </Text>
        )}
      </div>
    </Banner>
  );
}
